<template>
  <div>
    <draggable
      v-model="reOrderedCriteriaArray"
      tag="ul"
      v-bind="dragOptions"
      @start="drag = true"
      @end="drag = false"
      @change="handleCriteriaReorder"
    >
      <li
        class="is-draggable audit-criteria-list"
        :draggable="!dragOptions.disabled"
        v-for="(criteria, index) in reOrderedCriteriaArray"
        :key="`criteria_${index}`"
      >
        <div
          :class="[$style.sectionCard]"
          class="card margin-top position-relative margin-bottom20"
        >
          <div :class="[$style.draggableIcon]">
            <div :class="[$style.displayIcon]">
              <img src="@/assets/images/ic-drag-blue.png" class="drag-icon" />
            </div>
          </div>

          <!-- Resume Edit overlay when criteria is being edited and modal is minimized -->
          <div
            v-if="editingCriteriaIndex === index && isEditModalMinimized"
            class="shared-list-item-edit-overlay"
            @click="handleRestoreModal"
          >
            <span class="shared-list-item-overlay-text"
              >Resume Edit <i class="mdi mdi-arrow-expand"></i
            ></span>
          </div>

          <div class="card-content margin-left24 padding-right20">
            <div :class="$style.criteriaHeader">
              <div>
                <template
                  v-if="criteria.document !== undefined"
                  class="column is-2 has-text-right"
                >
                  <a
                    v-if="criteria.document.id"
                    :class="['text-size', $style.documentLink]"
                    target="_blank"
                    :href="`/documents/${criteria.document.id}`"
                  >
                    {{ criteria.document.id }}
                    <b-icon
                      size="is-small"
                      pack="fas"
                      icon="external-link-alt"
                    ></b-icon>
                  </a>
                </template>
              </div>
              <div
                :class="[
                  $style.criteriaActionContainer,
                  'column',
                  'is-3',
                  'has-text-right',
                ]"
              >
                <div class="block">
                  <a
                    :class="$style.guideBtn"
                    @click="openCriteriaGuideModal(criteria, index)"
                  >
                    <b-tooltip
                      multilined
                      :label="guideTooltipLabel"
                      position="is-bottom"
                    >
                      <b-icon
                        icon="sticky-note"
                        pack="fas"
                        size="is-small"
                      ></b-icon>
                    </b-tooltip>
                  </a>
                  <a
                    :class="$style.editBtn"
                    @click="openCriteriaEditModal(criteria, index)"
                    v-if="editable && !isAuditCycleCommencedOrCompleted"
                  >
                    <b-icon icon="pencil" size="is-small"></b-icon>
                  </a>
                  <a
                    :class="$style.deletebtn"
                    @click="removeCriteria(index)"
                    v-if="
                      editable &&
                      reOrderedCriteriaArray.length > 1 &&
                      !isAuditCycleCommencedOrCompleted
                    "
                  >
                    <b-icon icon="delete" size="is-small"></b-icon>
                  </a>
                </div>
              </div>
            </div>
            <div class="columns no-bottom-margin">
              <div
                class="column is-9 text-size text-color padding-bottom8 bold"
              >
                <span :class="$style.criteriaTitleField">
                  {{ criteria.title }}
                </span>
                <b-tag
                  v-if="
                    (criteria.document !== undefined &&
                      criteria.document.id === '') ||
                    criteria.isCustom === true ||
                    criteria.isChanged === true
                  "
                  type="is-primary"
                  class="margin-left12"
                >
                  <p class="is-text-info">CUSTOM</p>
                </b-tag>
                <b-tag
                  v-else-if="criteria.updatedJbiCriteria"
                  type="is-primary"
                  class="mt2 cursor-pointer"
                >
                  <p
                    @click="openJbiCriteriaUpdateModal(criteria)"
                    class="is-text-info"
                  >
                    {{ isProject ? 'VIEW UPDATE' : 'REVIEW UPDATE' }}
                  </p>
                </b-tag>
              </div>
            </div>
            <div :class="[$style.criteriaOptions, 'pbxs']">
              <template v-if="criteria.type === CRITERION_TYPE.BOOLEAN">
                <div
                  :class="$style.criteriaBooleanOptions"
                  v-for="(booleanOption, index) in criteria.booleanOptions"
                  :key="`${index}${booleanOption}`"
                >
                  <b-radio
                    disabled
                    native-value="booleanOption"
                    size="is-small"
                  >
                    <span :class="$style.optionText">
                      {{ booleanOption }}
                    </span>
                  </b-radio>
                </div>
              </template>

              <template v-if="criteria.type === CRITERION_TYPE.RANGE">
                <div :class="$style.criteriaRangeOptions">
                  Min: {{ criteria.range[0] }}
                </div>
                <div :class="$style.criteriaRangeOptions">
                  Max: {{ criteria.range[1] }}
                </div>
                <div :class="$style.criteriaRangeOptions">
                  N/A: {{ criteria.range[2] }}
                </div>
              </template>

              <template v-if="criteria.type === CRITERION_TYPE.CHECKBOXES">
                <div
                  :class="$style.criteriaCheckBoxOptions"
                  v-for="(
                    checkboxesOption, checkboxesOptionIndex
                  ) in criteria.checkboxesOptions"
                  :key="checkboxesOptionIndex"
                >
                  <b-checkbox disabled type="is-info">
                    <span :class="$style.optionText">{{
                      checkboxesOption
                    }}</span>
                  </b-checkbox>
                </div>
              </template>

              <template
                v-if="criteria.type === CRITERION_TYPE.CHECKBOXES_SINGLE"
              >
                <div
                  :class="$style.criteriaCheckBoxOptions"
                  v-for="(
                    checkboxesOption, checkboxesOptionIndex
                  ) in criteria.checkboxesOptions"
                  :key="checkboxesOptionIndex"
                >
                  <b-checkbox disabled type="is-info">
                    <span :class="$style.optionText">{{
                      checkboxesOption
                    }}</span>
                  </b-checkbox>
                </div>
              </template>

              <template
                v-if="criteria.type === CRITERION_TYPE.CONTINUOUS_DISCRETE"
              >
                <div
                  :class="$style.continuousDiscreteRangeContainer"
                  v-if="isContinuousDiscreteRangeEnabled(criteria)"
                >
                  <span>Min: {{ getRangeValue('min', criteria) }}</span>
                  <span>Max: {{ getRangeValue('max', criteria) }}</span>
                </div>
              </template>
            </div>

            <div class="columns">
              <div :class="[$style.tagContainer, 'column']">
                <template>
                  <div
                    v-for="(tag, tagIndex) in getTags(criteria.tags)"
                    :key="tagIndex"
                  >
                    <span :class="[$style.tag, 'padding-right']">{{
                      tag
                    }}</span>
                  </div>
                </template>
              </div>
              <div class="column align-right">
                <template
                  v-if="criteria.document === undefined"
                  class="column is-2 has-text-right"
                >
                  <a :class="$style.documentLink"> Custom Criteria </a>
                </template>
              </div>
            </div>
          </div>
        </div>
      </li>
    </draggable>
    <AuditCriteriaCreateModal
      v-if="isEditModalActive && editingCriteriaIndex !== null"
      :key="editModalKey"
      :modalTitle="'Edit Criteria'"
      :criterion="criterionForEdit"
      :reOrderedCriteriaArray="reOrderedCriteriaArray"
      :criteriaIndex="editingCriteriaIndex"
      :currentCriteria="reOrderedCriteriaArray[editingCriteriaIndex]"
      :projectEdit="projectEdit"
      :createProject="createProject"
      :isProject="isProject"
      :isModalActive="isEditModalActive"
      :isMinimized="isEditModalMinimized"
      :projectId="projectId"
      :auditId="auditId"
      @close="handleCloseModal"
      @minimize="handleMinimizeModal"
      @restore="handleRestoreModal"
      @updateCriteria="handleUpdateCriteria"
      @updateReOrderedCriteriaArray="handleUpdateReOrderedCriteriaArray"
    />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import BaseModal from '@/jbi-shared/vue-components/BaseModal.vue';
import draggable from 'vuedraggable';
import {
  CplusCriteria,
  UpdateCriteriaPayload,
} from '@/store/modules/projects/types/projects.types';
import { cloneDeep as _cloneDeep, orderBy as _orderBy } from 'lodash';
import AuditCriteriaCreateModal from '@/views/Project/components/AuditCriteriaCreateModal.vue';
import CriteriaGuideModal from '@/components/CriteriaGuideModal.vue';
import {
  BooleanCriterionContent,
  CheckboxesCriterionContent,
  CheckboxesSingleCriterionContent,
  Criterion,
  CRITERION_TYPE,
  RangeCriterionContent,
  ContinuousDiscreteCriterionContent,
} from '@/store/types/criterions.types';
import {
  isDifferentDeep,
  isTruthy,
} from '@/jbi-shared/util/watcher.vue-decorator';
import JbiCriteriaUpdateModal from '../../../views/Project/components/JbiCriteriaUpdateModal.vue';
import { Action, namespace, State } from 'vuex-class';
import { ToastProgrammatic as Toast } from 'buefy';
import { ApiState } from '../../../store/types/general.types';
import { UpdateSingleCriteriaPayload } from '@/store/modules/audits/types/audits.types';
import { RootState } from '@/store/store';
import InfoModal from '@/components/InfoModal.vue';
import DeleteModalBox from '@/components/DeleteModalBox.vue';

const unsavedChangesGuard = namespace('unsavedChangesGuard');

@Component({
  components: {
    BaseModal,
    draggable,
    AuditCriteriaCreateModal,
    CriteriaGuideModal,
    InfoModal,
    DeleteModalBox,
  },
  data: () => {
    return {
      CRITERION_TYPE, // Keep this for backwards compatibility and for non-class components
    };
  },
})
export default class AuditCriteriaList extends Vue {
  // IMPORTANT: This class property declaration is required for TypeScript class components
  // to properly access CRITERION_TYPE in the template. The data() option with arrow function
  // can cause scope issues. This dual declaration ensures it works in all contexts.
  public CRITERION_TYPE = CRITERION_TYPE;

  @Prop()
  public selectedCriteriaArray!: CplusCriteria[];
  @Prop({ default: null })
  public projectId!: number;
  @Prop({ default: null })
  public auditId!: number;
  @Prop({ default: false })
  public createProject!: boolean;
  @Prop({ default: false })
  public isProject!: boolean;
  @Prop({
    default() {
      return false;
    },
  })
  public projectEdit!: boolean;
  @Prop({ default: true })
  public editable!: boolean;
  @Prop({ type: Boolean, default: false })
  public isAuditCycleCommencedOrCompleted!: boolean;

  public reOrderedCriteriaArray: CplusCriteria[] = [];
  public drag = false;
  public guideTooltipLabel = 'Add, edit, or delete a guide for this criteria';
  public removedCriteriaIndex = -1;

  public editingCriteriaIndex: number | null = null; // To know which item is being edited for overlay
  public criterionForEdit: CplusCriteria | null = null; // Data for the modal
  public isEditModalActive: boolean = false; // Controls modal visibility/state for our logic
  public isEditModalMinimized: boolean = false; // Controls minimized state for our logic
  private editModalKey: number = 0; // To help re-render modal if needed

  public dragOptions = {
    animation: 200,
    group: 'description',
    disabled: !this.editable,
    ghostClass: 'draggable__ghost',
    chosenClass: 'draggable__chosen',
    dragClass: 'draggable__drag',
    forceFallback: false, // it cause weird drag behaviour when set it to true
  };

  @unsavedChangesGuard.State('userSelectedKeepEditing')
  public userSelectedKeepEditing!: boolean;

  @unsavedChangesGuard.Action('resetKeepEditingFlag')
  public resetKeepEditingFlag!: () => void;

  @Action('projects/getProjectCriteria')
  public getProjectCriteria!: (projectId: number) => void;

  @Action('audits/getAuditCriteria')
  public getAuditCriteria!: (auditId: number) => void;

  @Action('projects/updateProjectCriteriaById')
  public updateProjectCriteriaById!: (payload: UpdateCriteriaPayload) => void;

  @State((state) => state.projects.apiState.updateProjectCriteriaById)
  public updateProjectCriteriaByIdState!: ApiState;

  @Action('projects/deleteProjectCriteriaById')
  public deleteProjectCriteriaById!: (payload: {
    projectId: number;
    criteriaId: number;
  }) => void;

  @State((state) => state.projects.apiState.deleteProjectCriteriaById)
  public deleteProjectCriteriaByIdState!: ApiState;

  @Action('audits/updateAuditCriteriaById')
  public updateAuditCriteriaById!: (
    payload: UpdateSingleCriteriaPayload,
  ) => void;

  @State((state) => state.audits.apiState.updateAuditCriteriaById)
  public updateAuditCriteriaByIdState!: ApiState;

  @Action('audits/deleteAuditCriteriaById')
  public deleteAuditCriteriaById!: (payload: {
    auditId: number;
    criteriaId: number;
  }) => void;

  @State((state: RootState) => state.audits.apiState.deleteAuditCriteriaById)
  public deleteAuditCriteriaByIdState!: ApiState;

  @Action('projects/getUpdatedJBICriteriaWithAcknowledgement')
  public getUpdatedJBICriteriaWithAcknowledgement!: (projectId: number) => void;

  // Sorts by order and id to ensure consistent rendering
  get selectedCriteriaList() {
    return this.selectedCriteriaArray;
  }

  public deleteModal(confirmButtonText: string, modalContent: string): void {
    this.$buefy.modal.open({
      parent: this,
      component: DeleteModalBox,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Remove criteria?',
        modalContent,
        confirmButtonText,
      },
      events: {
        confirm: () => {
          const criteria: CplusCriteria = this.reOrderedCriteriaArray[
            this.removedCriteriaIndex
          ];
          if (criteria.criteriaDetails) {
            if (this.isProject) {
              this.deleteProjectCriteriaById({
                projectId: this.projectId,
                criteriaId: criteria.criteriaDetails.id,
              });
            } else {
              this.deleteAuditCriteriaById({
                auditId: this.auditId,
                criteriaId: criteria.criteriaDetails.id,
              });
            }
          } else {
            this.emitChanges();
          }
        },
      },
    });
  }

  public infoModal(confirmButtonText: string, modalContent: string): void {
    const Modal = this.$buefy.modal.open({
      parent: this,
      component: InfoModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Cannot remove criteria',
        modalContent,
        confirmButtonText,
      },
      events: {
        confirm: () => {
          Modal.close();
        },
        close: () => {
          Modal.close();
        },
      },
    });
  }

  public removeCriteria(index: number) {
    const criteria: CplusCriteria = this.reOrderedCriteriaArray[index];
    const isCriteriaAddedIntoAuditCycles = Boolean(
      criteria.auditCycles && criteria.auditCycles.length,
    );
    this.removedCriteriaIndex = index;
    let confirmationButtonText = 'Remove';
    let confirmationText = `<p>Are you sure you want to remove this Criterion?<p>`;

    if (this.isProject) {
      confirmationText = `<p>Removing this criterion will also result in its removal from the upcoming Data Collection Period(s). Are you sure you want to remove this Criterion?<p>`;

      if (isCriteriaAddedIntoAuditCycles) {
        let AffectedAuditCycleNames = '';
        criteria.auditCycles.forEach((auditCycle: string) => {
          AffectedAuditCycleNames += `<li>${auditCycle}</li>`;
        });

        confirmationButtonText = 'Ok';
        confirmationText = `
        <p>This is the only criteria in the upcoming data collection periods. Please add another criteria before removing it.</p>
        <p style="margin-top: 12px;"><b>Affected data collection periods:</b></p>
        <ul style="list-style: disc; padding: 0.25rem 1rem; max-height: 100px; overflow: scroll;">
          ${AffectedAuditCycleNames}
        </ul>`;
      }
    }

    if (isCriteriaAddedIntoAuditCycles) {
      this.infoModal(confirmationButtonText, confirmationText);
    } else {
      this.deleteModal(confirmationButtonText, confirmationText);
    }
  }

  public mounted() {
    this.reOrderedCriteriaArray = _cloneDeep(this.selectedCriteriaList);
  }

  public getRangeValue(type: string, criteria: any): string {
    const config =
      this.createProject && Object.keys(criteria).includes('config')
        ? criteria.config
        : criteria.criteriaDetails && criteria.criteriaDetails.config
        ? criteria.criteriaDetails.config
        : null;

    const configArray = typeof config === 'string' ? config.split(',') : config;
    if (type === 'min') {
      return configArray[0];
    } else {
      return configArray[1];
    }
  }

  public isContinuousDiscreteRangeEnabled(criteria: any): boolean {
    const config =
      this.createProject && Object.keys(criteria).includes('config')
        ? criteria.config
        : criteria.criteriaDetails && criteria.criteriaDetails.config
        ? criteria.criteriaDetails.config
        : null;

    return config ? true : false;
  }

  /**
   * Open AuditCriteriaCreateModal.
   * This modal is used to add new criteria and also edit existing criteria.
   */
  public openCriteriaAddModal() {
    this.$buefy.modal.open({
      parent: this,
      component: AuditCriteriaCreateModal,
      hasModalCard: true,
      trapFocus: true,
      customClass: 'transparent-modal-background',
      props: {
        modalTitle: 'Add New Criteria',
        reOrderedCriteriaArray: this.reOrderedCriteriaArray,
        isProject: this.isProject,
        projectId: this.projectId,
        auditId: this.auditId,
      },
      events: {
        updateReOrderedCriteriaArray: (newValue: any) => {
          this.reOrderedCriteriaArray = newValue;
          this.$emit(
            'update:selectedCriteriaArray',
            this.reOrderedCriteriaArray,
          );
        },
      },
    });
  }

  public openCriteriaGuideModal(
    criteria: CplusCriteria,
    criteriaIndex: number,
  ) {
    this.$buefy.modal.open({
      parent: this,
      component: CriteriaGuideModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        currentCriteria: criteria,
        criteriaIndex,
        projectId: this.projectId,
        auditId: this.auditId,
        createProject: this.createProject,
      },
      events: {
        updateCriteriaGuide: (newValue: any) => {
          this.reOrderedCriteriaArray[criteriaIndex] = newValue;
          this.$emit(
            'update:selectedCriteriaArray',
            this.reOrderedCriteriaArray,
          );
        },
      },
    });
  }

  public openCriteriaEditModal(criteria: CplusCriteria, criteriaIndex: number) {
    let criterion: Criterion;
    if (criteria.type === CRITERION_TYPE.BOOLEAN) {
      criterion = new Criterion({
        content: new BooleanCriterionContent({
          title: criteria.title,
          type: CRITERION_TYPE.BOOLEAN,
          booleanOptions: criteria.booleanOptions,
          sourceLink: criteria.sourceLink,
        }),
      });
    } else if (criteria.type === CRITERION_TYPE.RANGE) {
      criterion = new Criterion({
        content: new RangeCriterionContent({
          title: criteria.title,
          type: CRITERION_TYPE.RANGE,
          range: criteria.range,
          sourceLink:
            criteria.sourceLink !== undefined ? criteria.sourceLink : '',
        }),
      });
    } else if (criteria.type === CRITERION_TYPE.CHECKBOXES_SINGLE) {
      criterion = new Criterion({
        content: new CheckboxesSingleCriterionContent({
          title: criteria.title,
          type: CRITERION_TYPE.CHECKBOXES_SINGLE,
          checkboxesOptions: criteria.checkboxesOptions,
          sourceLink:
            criteria.sourceLink !== undefined ? criteria.sourceLink : '',
        }),
      });
    } else if (criteria.type === CRITERION_TYPE.CHECKBOXES) {
      criterion = new Criterion({
        content: new CheckboxesCriterionContent({
          title: criteria.title,
          type: CRITERION_TYPE.CHECKBOXES,
          checkboxesOptions: criteria.checkboxesOptions,
          sourceLink:
            criteria.sourceLink !== undefined ? criteria.sourceLink : '',
        }),
      });
    } else {
      const criteriaConfig = criteria.config
        ? criteria.config
        : criteria.criteriaDetails && criteria.criteriaDetails.config
        ? criteria.criteriaDetails.config
        : null;

      const config =
        typeof criteriaConfig === typeof 'string'
          ? criteriaConfig.split(', ')
          : criteriaConfig;
      criterion = new Criterion({
        content: new ContinuousDiscreteCriterionContent({
          title: criteria.title,
          type: CRITERION_TYPE.CONTINUOUS_DISCRETE,
          config,
          sourceLink:
            criteria.sourceLink !== undefined ? criteria.sourceLink : '',
        }),
      });
    }

    this.criterionForEdit = { ...criteria, content: criterion.content };
    this.editingCriteriaIndex = criteriaIndex;
    this.isEditModalMinimized = false; // Ensure it's not minimized on open
    this.isEditModalActive = true;
  }

  public handleCloseModal(): void {
    this.isEditModalActive = false;
    this.isEditModalMinimized = false;
    this.criterionForEdit = null;
    this.editingCriteriaIndex = null;
    this.editModalKey += 1; // Force re-mount of modal component to reset its internal state
  }

  public handleMinimizeModal(): void {
    this.isEditModalMinimized = true;
  }

  public handleRestoreModal(): void {
    this.isEditModalMinimized = false;
  }

  public handleCriteriaReorder(event: any) {
    this.reOrderedCriteriaArray.forEach((criteria, index) => {
      criteria.order = index + 1;
    });
    this.$emit('update:selectedCriteriaArray', this.reOrderedCriteriaArray);
  }

  public handleUpdateReOrderedCriteriaArray(newValue: any) {
    this.reOrderedCriteriaArray = newValue;
    this.$emit('update:selectedCriteriaArray', this.reOrderedCriteriaArray);
    this.$emit('updateCriteriaHandler');
    // Close the modal after the update is emitted
    this.handleCloseModal();
  }

  public handleUpdateCriteria(payload: {
    projectId?: number;
    newValue: CplusCriteria;
    applyChanges: boolean;
    selectedAuditIds: number[];
  }): void {
    const { newValue, applyChanges, selectedAuditIds } = payload;
    if (this.isProject) {
      this.updateProjectCriteriaById({
        projectId: this.projectId,
        criteria: newValue,
        selectedAuditIds: selectedAuditIds,
      });
    } else {
      this.updateAuditCriteriaById({
        currentAuditId: this.auditId,
        selectedAuditIds: [...new Set([...selectedAuditIds, this.auditId])],
        criteria: newValue,
      });
    }
    // Close the modal after the update is emitted
    this.handleCloseModal();
  }

  @Watch('selectedCriteriaArray')
  @isTruthy
  @isDifferentDeep
  public watchSelectedCriteriaArray() {
    this.reOrderedCriteriaArray = _cloneDeep(this.selectedCriteriaList);
  }

  public getTags(tagInfo: any) {
    if (Array.isArray(tagInfo)) {
      return tagInfo;
    } else {
      const tags: string[] = [];
      if (tagInfo) {
        Object.keys(tagInfo).forEach((key: string) => {
          const tag = tagInfo[key];
          tag.forEach((value: string) => {
            tags.push(value);
          });
        });
      }
      return tags;
    }
  }

  criteriaUpdatedToast() {
    Toast.open({
      message: `Criteria successfully updated.`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
  }

  criteriaDeletedToast() {
    Toast.open({
      message: `Criteria successfully deleted.`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
  }

  @Watch('updateProjectCriteriaByIdState.success')
  @isTruthy
  public watchUpdateProjectCriteriaByIdState() {
    this.getProjectCriteria(this.projectId);
    this.getUpdatedJBICriteriaWithAcknowledgement(this.projectId);
    this.criteriaUpdatedToast();
  }

  @Watch('updateAuditCriteriaByIdState.success')
  @isTruthy
  public watchUpdateAuditCriteriaByIdState(): void {
    this.getAuditCriteria(this.auditId);
    this.criteriaUpdatedToast();
  }

  @Watch('deleteProjectCriteriaByIdState.success')
  @isTruthy
  public watchDeleteProjectCriteriaByIdState() {
    this.getProjectCriteria(this.projectId);
    this.emitChanges();
    this.criteriaDeletedToast();
  }

  @Watch('deleteAuditCriteriaByIdState.success')
  @isTruthy
  public watchDeleteAuditCriteriaByIdState() {
    this.getAuditCriteria(this.auditId);
    this.emitChanges();
    this.criteriaDeletedToast();
  }

  public emitChanges() {
    if (this.removedCriteriaIndex > -1) {
      this.$emit(
        'updateDeletedCriteriaList',
        this.reOrderedCriteriaArray[this.removedCriteriaIndex],
      );
      this.reOrderedCriteriaArray.splice(this.removedCriteriaIndex, 1);
      this.$emit('update:selectedCriteriaArray', this.reOrderedCriteriaArray);
    }
  }

  @Watch('userSelectedKeepEditing')
  public onUserSelectedKeepEditing(newValue: boolean): void {
    if (newValue && this.isEditModalActive && this.isEditModalMinimized) {
      this.handleRestoreModal();
    }
    // Always reset the flag after checking it
    this.resetKeepEditingFlag();
  }

  public openJbiCriteriaUpdateModal(criteria: CplusCriteria): void {
    this.$buefy.modal.open({
      parent: this,
      component: JbiCriteriaUpdateModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        criteria,
      },
    });
  }

  // get isAuditEligibleForEdit(): boolean {
  //   return this.isProject
  // }
}
</script>

<style lang="scss" module>
.sectionCard {
  background-color: $white;
  border: 1px solid $grey-border;
  border-radius: 4px;
  box-shadow: unset;
  margin-bottom: 20px;
  & .sectionCardContent {
    padding: 0.8rem;
    padding-left: 0;
    min-height: 4rem;
    display: flex;
    flex-basis: 90%;
    align-items: center;
    border-bottom: 0.5px solid #dbdbdb;
  }

  .sectionCardRemoveBtn {
    display: flex;
    visibility: hidden;

    .btnRemoveSection {
      flex-basis: 24px;
      padding: 2px;
      padding-right: 6px;
      height: 100%;
      align-items: center;
      color: $grey;
      font-size: 10px;
      border: 0;
      cursor: pointer;
      background-color: inherit;
    }
  }

  & .draggableIcon {
    min-width: 16px;
    line-height: 0;
    max-width: 16px;
    position: absolute;
    height: 100%;
    background-color: $hover-blue;
    border-radius: 4px 0px 0px 4px;

    & .displayIcon {
      width: 24px;
      line-height: 0;
      flex-basis: 10%;
      display: flex;
      padding: 2px 4px 2px 4px;
      align-items: center;
      height: 100%;
    }
  }

  :global(li.is-draggable:not(.draggable__ghost)) &:hover {
    cursor: move;
    background-color: $hover-blue;

    .draggableIcon {
      cursor: move;
    }

    & .sectionCardRemoveBtn {
      visibility: visible;
    }
  }
}
.openEditModal {
  display: block;
}

.guideBtn {
  color: $secondary;
  padding-right: 12px;
}
.editBtn {
  color: $secondary;
  padding-right: 12px;
}
.deletebtn {
  color: $secondary;
}
.tag {
  background: none;
  color: $font-grey;
  font-size: $size-6;
  text-transform: capitalize;
}

.documentLink {
  color: $secondary;
  font-size: $size-5;
  font-weight: $weight-bold;
  padding-right: unset;
}

.optionText {
  color: $dark !important;
  font-size: $size-5 !important;
  line-height: 17px;
  text-wrap: nowrap;
}

.criteriaOptions {
  color: $grey;
}

.criteriaTitleField {
  font-weight: $weight-semibold;
}

.updateAvailableTag {
  cursor: pointer;
}
.criteriaHeader {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.criteriaActionContainer {
  padding-top: unset;
  padding-right: unset;
}
.tagContainer {
  display: flex;
  flex-direction: row;
}
.continuousDiscreteRangeContainer {
  display: flex;
  flex-direction: column;
  span {
    &:first-child {
      margin-bottom: 10px;
      margin-top: 10px;
    }
  }
}
.continuousDiscretePrecisionContainer {
  margin-top: 10px;
}
</style>
